import type { SecurityClearanceError } from "@features/port-security/components/SecurityClearence.types.ts";
import type { CompleteSecurityClearancePayload } from "@services/security-clearance/types.ts";
import { completeSecurityClearanceById } from "@services/securityClearanceApi";
import { useMutation } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";

const getCompleteSecurityClearanceAction = (payload: CompleteSecurityClearancePayload): string => {
  if (payload.status?.includes("APPROVED")) return "approve_security_clearance";
  if (payload.status?.includes("REJECTED")) return "reject_security_clearance";
  if (payload.status?.includes("RESUBMIT")) return "request_security_clearance_resubmission";
  return "complete_security_clearance";
};

export const useCompleteSecurityClearance = ({
  handleSuccess,
  handleError,
}: {
  handleSuccess: () => void;
  handleError: (error: SecurityClearanceError) => void;
}) => {
  const completeSecurityClearanceMutation = useMutation({
    mutationFn: (variables: Parameters<typeof completeSecurityClearanceById>[0]) =>
      runTransaction(
        {
          module: "security_clearance",
          screen: "SecurityClearancePreview",
          action: getCompleteSecurityClearanceAction(variables.payload),
          entityType: "security_clearance",
        },
        (transactionId) =>
          completeSecurityClearanceById({
            ...variables,
            ...(transactionId ? { transactionId } : {}),
          }),
      ),
    onSuccess: () => handleSuccess(),
    onError: (error: SecurityClearanceError) => handleError(error),
  });

  const completeSecurityClearance = async ({
    id,
    service,
    payload,
  }: {
    id: string;
    service: string;
    payload: CompleteSecurityClearancePayload;
  }) => {
    if (payload.status?.includes("RESUBMIT") && !payload.reason?.trim()) {
      return;
    }

    completeSecurityClearanceMutation.mutate({
      id,
      service,
      payload,
    });
  };

  return {
    completeSecurityClearance,
  };
};
